import type { InputHTMLAttributes, KeyboardEvent, MouseEvent } from "react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import intl from "react-intl-universal";
import { DisplayButton, EditorInput, ErrorMessage, Root } from "./styled";

type ValidationResult = string | boolean | null | undefined;

export interface InlineEditableTextProps<TValue> {
  readonly value: TValue;
  readonly onCommit: (value: TValue) => void | Promise<void>;
  readonly onCancel?: () => void;
  readonly validate?: (value: TValue, rawValue: string) => ValidationResult;
  readonly parse?: (rawValue: string) => TValue;
  readonly formatDisplay?: (value: TValue) => string;
  readonly editable?: boolean;
  readonly placeholder?: string;
  readonly ariaLabel?: string;
  readonly type?: InputHTMLAttributes<HTMLInputElement>["type"];
  readonly inputMode?: InputHTMLAttributes<HTMLInputElement>["inputMode"];
  readonly className?: string;
  readonly commitOnBlur?: boolean;
}

const getFallbackDisplayValue = (value: unknown) =>
  value === null || value === undefined ? "" : String(value);

const getValueErrorMessage = (validationResult: ValidationResult) => {
  if (typeof validationResult === "string") {
    return validationResult;
  }

  if (validationResult === false) {
    return intl.get("commons.table.inlineEditableText.invalidValue");
  }

  return null;
};

const isNumericInput = (
  type: InputHTMLAttributes<HTMLInputElement>["type"],
  inputMode: InputHTMLAttributes<HTMLInputElement>["inputMode"]
) => type === "number" || inputMode === "numeric" || inputMode === "decimal";

const isAllowedNumericKey = (key: string) => {
  if (key.length !== 1) {
    return true;
  }

  return /[\d,.-]/.test(key);
};

const sanitizeNumericValue = (value: string) => value.replace(/[^\d,.-]/g, "");

export function InlineEditableText<TValue>({
  value,
  onCommit,
  onCancel,
  validate,
  parse,
  formatDisplay,
  editable = true,
  placeholder,
  ariaLabel,
  type = "text",
  inputMode,
  className,
  commitOnBlur = true,
}: InlineEditableTextProps<TValue>) {
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingByClick, setIsEditingByClick] = useState(false);
  const [draft, setDraft] = useState(() => getFallbackDisplayValue(value));
  const [error, setError] = useState<string | null>(null);
  const errorMessageId = useId();
  const ignoreNextBlurRef = useRef(false);
  const isEditingByClickRef = useRef(false);
  const mouseEnterTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const numericInput = isNumericInput(type, inputMode);

  const displayValue = useMemo(
    () => (formatDisplay ? formatDisplay(value) : getFallbackDisplayValue(value)),
    [formatDisplay, value]
  );

  useEffect(() => {
    if (!isEditing) {
      setDraft(getFallbackDisplayValue(value));
    }
  }, [isEditing, value]);

  useEffect(
    () => () => {
      if (mouseEnterTimeoutRef.current) {
        clearTimeout(mouseEnterTimeoutRef.current);
      }
    },
    []
  );

  const clearMouseEnterTimeout = () => {
    if (mouseEnterTimeoutRef.current) {
      clearTimeout(mouseEnterTimeoutRef.current);
      mouseEnterTimeoutRef.current = null;
    }
  };

  const closeEditor = (shouldNotifyCancel = false) => {
    clearMouseEnterTimeout();
    ignoreNextBlurRef.current = true;
    isEditingByClickRef.current = false;
    setIsEditing(false);
    setIsEditingByClick(false);
    setError(null);
    setDraft(getFallbackDisplayValue(value));

    if (shouldNotifyCancel) {
      onCancel?.();
    }
  };

  const commitDraft = async () => {
    const parsedValue = parse ? parse(draft) : (draft as unknown as TValue);
    const validationResult = validate?.(parsedValue, draft);
    const validationError = getValueErrorMessage(validationResult);

    if (validationError) {
      setError(validationError);
      return;
    }

    if (draft === getFallbackDisplayValue(value)) {
      closeEditor();
      return;
    }

    try {
      await Promise.resolve(onCommit(parsedValue));
      closeEditor();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : intl.get("commons.table.inlineEditableText.saveError");
      setError(message);
    }
  };

  const handleDisplayClick = () => {
    if (!editable) {
      return;
    }

    clearMouseEnterTimeout();
    ignoreNextBlurRef.current = false;
    isEditingByClickRef.current = true;
    setError(null);
    setDraft(getFallbackDisplayValue(value));
    setIsEditingByClick(true);
    setIsEditing(true);
  };

  const handleEditorClick = () => {
    ignoreNextBlurRef.current = false;
    isEditingByClickRef.current = true;
    setIsEditingByClick(true);
  };

  const handleMouseEnter = () => {
    if (isEditing || isEditingByClickRef.current || mouseEnterTimeoutRef.current) {
      return;
    }

    mouseEnterTimeoutRef.current = setTimeout(() => {
      mouseEnterTimeoutRef.current = null;
      setIsEditing(true);
    }, 500);
  };

  const handleMouseLeave = (event: MouseEvent<HTMLDivElement>) => {
    const relatedTarget = event.relatedTarget;

    if (relatedTarget instanceof Node && event.currentTarget.contains(relatedTarget)) {
      return;
    }

    clearMouseEnterTimeout();

    if (!isEditingByClickRef.current) {
      setIsEditing(false);
    }
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      void commitDraft();
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      closeEditor(true);
      return;
    }

    if (numericInput && !event.ctrlKey && !event.metaKey && !event.altKey) {
      const navigationKeys = new Set([
        "Backspace",
        "Delete",
        "Tab",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
      ]);

      if (navigationKeys.has(event.key)) {
        return;
      }

      if (!isAllowedNumericKey(event.key)) {
        event.preventDefault();
      }
    }
  };

  const handleBlur = () => {
    if (ignoreNextBlurRef.current) {
      ignoreNextBlurRef.current = false;
      return;
    }

    if (!commitOnBlur) {
      closeEditor();
      return;
    }

    void commitDraft();
  };

  if (!editable) {
    return <Root className={className}>{displayValue || "-"}</Root>;
  }

  return (
    <Root
      className={className}
      data-editing-pinned={isEditingByClick ? "true" : undefined}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {isEditing ? (
        <EditorInput
          value={draft}
          placeholder={placeholder}
          aria-label={ariaLabel}
          type={type}
          inputMode={inputMode}
          autoFocus={isEditingByClick}
          $hasError={Boolean(error)}
          onFocus={(event) => {
            event.currentTarget.select();
          }}
          onClick={handleEditorClick}
          onChange={(event) => {
            const nextValue = numericInput
              ? sanitizeNumericValue(event.target.value)
              : event.target.value;

            setDraft(nextValue);
            if (error) {
              setError(null);
            }
          }}
          onBlur={handleBlur}
          onKeyDown={handleInputKeyDown}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorMessageId : undefined}
        />
      ) : (
        <DisplayButton
          type="button"
          onClick={handleDisplayClick}
          title={displayValue || "-"}
          aria-label={ariaLabel}
        >
          {displayValue || "-"}
        </DisplayButton>
      )}
      {error ? (
        <ErrorMessage id={errorMessageId} role="alert">
          {error}
        </ErrorMessage>
      ) : null}
    </Root>
  );
}

export default InlineEditableText;
